import { useState } from 'react';
import {
  Button,
  DatePicker,
  DropDown,
  FileUploadInput,
  FlexColumn,
  FormField,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { Controller, useFormContext } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { ReactComponent as ChevronDownIcon } from '@elementary/common-ui/assets/arrow-down.svg';
import { ReactComponent as ChevronUpIcon } from '@elementary/common-ui/assets/arrow-up.svg';
import styled from 'styled-components';
import dayjs from 'dayjs';
import type { ProjectField } from '../types';
import JiraDynamicFieldAutocomplete from './JiraDynamicFieldAutocomplete';
import JiraUserAutocompleteField from './JiraUserAutocompleteField';

const CollapseContent = styled(FlexColumn)`
  gap: 16px;
  overflow: clip;
  min-height: 0;
`;

const CollapseContainer = styled.div<{ $isCollapsed: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isCollapsed }) => ($isCollapsed ? '0fr' : '1fr')};
  transition: grid-template-rows 0.3s ease;
`;

type Props = {
  fields: ProjectField[];
  defaultValues?: Record<string, any>;
  integrationId: string;
  projectKey?: string | null;
  baseUrl?: string | null;
  projectId?: string;
  issueType?: string;
};

const JiraDynamicFields = ({
  fields,
  defaultValues,
  integrationId,
  projectKey,
  baseUrl,
  projectId,
  issueType,
}: Props) => {
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [autocompleteQueries, setAutocompleteQueries] = useState<
    Record<string, string>
  >({});
  const selectedEnvId = useSelectedEnv();
  const formMethods = useFormContext<Record<string, any>>();
  const { register, setValue, watch } = formMethods;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const debouncedQueries = useDebounce(autocompleteQueries, 300);

  const handleAutocompleteInputChange = (fieldKey: string, value: string) => {
    setAutocompleteQueries((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const renderField = (field: ProjectField) => {
    const fieldValue = watch(field.key);
    const defaultValue = defaultValues?.[field.key];

    const projectSettingsUrl =
      projectKey && baseUrl
        ? `${baseUrl}/plugins/servlet/project-config/${projectKey}/summary`
        : null;

    if (
      field.schema_info.type === 'array' &&
      field.schema_info.items === 'attachment'
    ) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
      const fileError = formMethods.formState.errors[field.key];

      return (
        <FormField
          key={field.key}
          title={field.name}
          optional={!field.required}
          errorMessage={fileError?.message as string}
        >
          <Controller
            name={field.key}
            control={formMethods.control}
            defaultValue={null}
            rules={{
              validate: (value) => {
                if (!value) return true;
                if (value instanceof File && value.size > MAX_FILE_SIZE) {
                  return `File size must be less than 10MB (current: ${(
                    value.size /
                    1024 /
                    1024
                  ).toFixed(2)}MB)`;
                }
                return true;
              },
            }}
            render={({ field: controllerField }) => (
              <FileUploadInput
                {...controllerField}
                placeholder="Upload files"
                sx={{ height: '40px !important' }}
                storeFileObject
              />
            )}
          />
        </FormField>
      );
    }

    const isUserField =
      field.schema_info.type === 'user' ||
      (field.schema_info.type === 'array' &&
        field.schema_info.items === 'user');

    if (isUserField && selectedEnvId && projectId) {
      return (
        <JiraUserAutocompleteField
          key={field.key}
          field={field}
          label={field.name}
          envId={selectedEnvId}
          integrationId={integrationId}
          projectId={projectId}
        />
      );
    }

    if (field.allowed_values && field.allowed_values?.length > 0) {
      const optionsWithIcons = field.allowed_values.map((option) => ({
        ...option,
        icon: option.icon ? (
          <img
            src={option.icon}
            alt={option.label}
            style={{ width: 16, height: 16 }}
          />
        ) : undefined,
      }));

      const isSprintField = field.name.toLowerCase() === 'sprint';
      const sprintDescription = isSprintField
        ? 'Notice: Only sprints that contain at least one issue are shown due to Jira permission limitations'
        : undefined;

      return (
        <FormField
          key={field.key}
          title={field.name}
          optional={!field.required}
          errorMessage={
            formMethods.formState.errors[field.key]?.message as string
          }
          description={sprintDescription}
        >
          <DropDown
            size="large"
            options={optionsWithIcons}
            value={fieldValue || defaultValue}
            onChange={(value) => {
              setValue(field.key, value);
              if (field.required) {
                formMethods.clearErrors(field.key);
              }
            }}
            placeholder={field.name}
            multiple={field.schema_info?.type === 'array'}
          />
        </FormField>
      );
    }

    if (field.schema_info.type === 'date') {
      const dateValue = fieldValue || defaultValue;
      const dayjsValue =
        dateValue && typeof dateValue === 'string'
          ? dayjs(dateValue)
          : dateValue;

      return (
        <FormField
          key={field.key}
          title={field.name}
          optional={!field.required}
          errorMessage={
            formMethods.formState.errors[field.key]?.message as string
          }
        >
          <DatePicker
            size="large"
            label={''}
            value={dayjsValue || null}
            onChange={(newValue) => {
              setValue(field.key, newValue ? newValue.toISOString() : null);
              if (field.required) {
                formMethods.clearErrors(field.key);
              }
            }}
          />
        </FormField>
      );
    }

    if (field.auto_complete_url && selectedEnvId) {
      return (
        <JiraDynamicFieldAutocomplete
          key={field.key}
          field={field}
          label={field.name}
          defaultValue={defaultValue}
          integrationId={integrationId}
          selectedEnvId={selectedEnvId}
          query={debouncedQueries[field.key] || ''}
          onInputChange={(value) =>
            handleAutocompleteInputChange(field.key, value)
          }
          onClick={stopPropagation}
          projectSettingsUrl={projectSettingsUrl}
          optional={!field.required}
          errorMessage={
            formMethods.formState.errors[field.key]?.message as string
          }
          projectId={projectId}
          issueType={issueType}
        />
      );
    }

    if (
      field.allowed_values !== undefined &&
      field.allowed_values?.length === 0 &&
      defaultValue
    ) {
      return (
        <FormField
          key={field.key}
          title={field.name}
          optional={!field.required}
          errorMessage={
            formMethods.formState.errors[field.key]?.message as string
          }
        >
          <TextField value={defaultValue} size="large" disabled />
        </FormField>
      );
    }

    if (field.schema_info.type === 'number') {
      return (
        <FormField
          key={field.key}
          title={field.name}
          optional={!field.required}
          errorMessage={
            formMethods.formState.errors[field.key]?.message as string
          }
        >
          <TextField
            type="number"
            defaultValue={defaultValue}
            size="large"
            placeholder={field.name}
            {...register(field.key, {
              required: field.required ? requiredLabel : false,
              valueAsNumber: true,
            })}
          />
        </FormField>
      );
    }

    return (
      <FormField
        key={field.key}
        title={field.name}
        optional={!field.required}
        errorMessage={
          formMethods.formState.errors[field.key]?.message as string
        }
      >
        <TextField
          defaultValue={defaultValue}
          size="large"
          placeholder={field.name}
          {...register(field.key, {
            required: field.required ? requiredLabel : false,
          })}
        />
      </FormField>
    );
  };

  return (
    <FlexColumn gap={2} mt={2} pb={2}>
      <Button
        onClick={() => setShowMoreFields((prev) => !prev)}
        variant="contained"
        color="secondary"
        sx={{ width: 'fit-content' }}
        endIcon={showMoreFields ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        Additional fields
      </Button>

      <CollapseContainer $isCollapsed={!showMoreFields}>
        <CollapseContent>{fields.map(renderField)}</CollapseContent>
      </CollapseContainer>
    </FlexColumn>
  );
};

export default JiraDynamicFields;
